// Web Push subscription hook.
//
// Drop in to any component that needs to prompt the user for notification
// permission and persist the resulting PushSubscription to the backend so the
// 2-hour event reminder scheduler can reach the user even when Allure is
// closed.
//
// Usage:
//   const { isSupported, isSubscribed, enable } = useWebPush();
//   <button onClick={enable}>Get reminders</button>
//
// `enable()` is idempotent — calling it again when already subscribed is a
// no-op. The hook silently no-ops on browsers that don't support push (older
// iOS Safari without home-screen install, Firefox in private mode, etc.).

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function useWebPush() {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window
      ? window.Notification.permission
      : 'default',
  );

  const isSupported =
    typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window;

  useEffect(() => {
    if (!isSupported) return undefined;
    let cancelled = false;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (!cancelled) setIsSubscribed(!!sub);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isSupported]);

  const registerSubscriptionWithBackend = useCallback(async (subscription) => {
    const token = localStorage.getItem('token');
    if (!token) return { ok: false, reason: 'logged_out' };
    if (!subscription) return { ok: false, reason: 'no_subscription' };

    await axios.post(
      `${API}/push/subscribe`,
      subscription.toJSON(),
      { headers: { Authorization: `Bearer ${token}` } },
    );
    setIsSubscribed(true);
    return { ok: true };
  }, []);

  const syncExistingSubscription = useCallback(async () => {
    if (!isSupported) return { ok: false, reason: 'unsupported' };
    if (window.Notification.permission !== 'granted') {
      setPermission(window.Notification.permission);
      return { ok: false, reason: window.Notification.permission === 'denied' ? 'denied' : 'permission_required' };
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        setIsSubscribed(false);
        return { ok: false, reason: 'no_subscription' };
      }
      return await registerSubscriptionWithBackend(subscription);
    } catch (err) {
      console.error('[useWebPush] sync existing subscription failed', err);
      return { ok: false, reason: 'sync_error' };
    }
  }, [isSupported, registerSubscriptionWithBackend]);

  const enable = useCallback(async () => {
    if (!isSupported) return { ok: false, reason: 'unsupported' };

    // Must be called from a user gesture (the bell click). Browsers reject
    // permission prompts that fire on page load.
    const result = await window.Notification.requestPermission();
    setPermission(result);
    if (result !== 'granted') return { ok: false, reason: 'denied' };

    const token = localStorage.getItem('token');
    if (!token) return { ok: false, reason: 'logged_out' };

    try {
      // Pull the VAPID public key from the backend so we don't bake it into
      // the bundle.
      const keyRes = await axios.get(`${API}/push/public-key`);
      const publicKey = keyRes.data?.public_key;
      if (!publicKey) return { ok: false, reason: 'missing_key' };

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      return await registerSubscriptionWithBackend(subscription);
    } catch (err) {
      // Most likely cause: user denied permission, or the browser doesn't
      // support push in this context (e.g. iOS Safari without home-screen
      // install). Either way, fail silently — caller can show its own UX.
      console.error('[useWebPush] enable failed', err);
      return { ok: false, reason: 'subscribe_error' };
    }
  }, [isSupported, registerSubscriptionWithBackend]);

  const disable = useCallback(async () => {
    if (!isSupported) return;
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        setIsSubscribed(false);
        return;
      }
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(
          `${API}/push/unsubscribe`,
          subscription.toJSON(),
          { headers: { Authorization: `Bearer ${token}` } },
        ).catch(() => {});
      }
      await subscription.unsubscribe();
      setIsSubscribed(false);
    } catch (err) {
      console.error('[useWebPush] disable failed', err);
    }
  }, [isSupported]);

  return {
    isSupported,
    permission,
    isSubscribed,
    enable,
    syncExistingSubscription,
    disable,
  };
}
